import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Plus, Check } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { BIOMARKERS, BRAND_INFO } from '../data/mock';
import { useCart } from '../context/CartContext';

const BiomarkersPage = () => {
  usePageTitle(
    `Build your panel — à la carte biomarkers | ${BRAND_INFO.name}`,
    'Browse 140+ individual biomarkers with live, lab-verified pricing. Build a panel of exactly the markers you care about — nothing more, nothing less.'
  );

  const [selected, setSelected] = useState(['tt', 'ft', 'e2', 'igf1', 'shbg']);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const navigate = useNavigate();
  const { addCustomPanel } = useCart();

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(BIOMARKERS.map((b) => b.category)))],
    []
  );

  const filtered = useMemo(() => {
    return BIOMARKERS.filter((b) => {
      if (category !== 'All' && b.category !== category) return false;
      if (query.trim() && !b.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [category, query]);

  const total = useMemo(
    () => BIOMARKERS.filter((b) => selected.includes(b.id)).reduce((s, b) => s + b.price, 0),
    [selected]
  );

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-10 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">Build your own</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900 max-w-4xl">
            Every marker you need.{' '}
            <em className="italic text-brick-500">None that you don&apos;t.</em>
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            Pick exactly the biomarkers you want. Live, lab-verified pricing — add them to a
            single requisition and get drawn at any partner location near you.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-12 gap-8">
          {/* Marker list */}
          <div className="lg:col-span-8">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search 140+ markers..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-cream-50 border border-cream-300 text-[14px]"
                />
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-4 py-3 rounded-full bg-cream-50 border border-cream-300 text-[14px]"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {filtered.map((b) => {
                const on = selected.includes(b.id);
                return (
                  <button
                    key={b.id}
                    onClick={() => toggle(b.id)}
                    className={`text-left flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border transition-all ${
                      on
                        ? 'bg-sage-50 border-sage-300'
                        : 'bg-cream-50 border-cream-200 hover:border-ink-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`shrink-0 w-5 h-5 rounded-md flex items-center justify-center ${
                          on ? 'bg-sage-500 text-white' : 'bg-cream-100 border border-cream-300'
                        }`}
                      >
                        {on ? (
                          <Check className="w-3 h-3" strokeWidth={3} />
                        ) : (
                          <Plus className="w-3 h-3 text-ink-500" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[14px] text-ink-900 truncate">{b.name}</div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-400 mt-0.5">
                          {b.category}
                        </div>
                      </div>
                    </div>
                    <span className="shrink-0 text-[14px] tabular-nums text-ink-700">${b.price}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sticky cart */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <h3 className="font-display text-2xl text-ink-900">Your panel</h3>
              <div className="mt-1 text-[12px] text-ink-500">
                {selected.length} marker{selected.length !== 1 ? 's' : ''} selected
              </div>

              <div className="mt-5 space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {selected.length === 0 ? (
                  <p className="text-[13px] text-ink-500">
                    No markers yet. Tap any marker on the left to add it.
                  </p>
                ) : (
                  selected.map((id) => {
                    const b = BIOMARKERS.find((x) => x.id === id);
                    if (!b) return null;
                    return (
                      <div
                        key={id}
                        className="flex items-center justify-between gap-2 py-2 border-b border-cream-200 last:border-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            onClick={() => toggle(id)}
                            className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-cream-100 hover:bg-cream-200 text-ink-600"
                            aria-label="Remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <span className="text-[13px] text-ink-800 truncate">{b.name}</span>
                        </div>
                        <span className="text-[13px] tabular-nums text-ink-700">${b.price}</span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="mt-5 pt-5 border-t border-cream-200 flex items-end justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">Total</div>
                  <div className="text-[12px] text-ink-500">All prices in USD</div>
                </div>
                <div className="font-display text-3xl text-ink-900">
                  <span className="text-lg align-top">$</span>
                  {total}
                </div>
              </div>
              <button
                disabled={selected.length === 0}
                onClick={() => {
                  const markers = BIOMARKERS.filter((b) => selected.includes(b.id));
                  if (markers.length === 0) return;
                  addCustomPanel(markers);
                  navigate('/cart');
                }}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brick-500 hover:bg-brick-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium px-5 py-3 rounded-full transition-colors"
              >
                Add panel to cart <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/panels"
                className="mt-2 block text-center text-[13px] text-ink-500 hover:text-brick-500"
              >
                or pick a curated panel
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BiomarkersPage;
